import React, { useEffect, useState } from "react";
import { FormGroup, Label, Input, Row, Col } from "reactstrap";
import { Upload, XCircle, Image as ImageIcon, FileText, AlertCircle, Film } from "react-feather";
import styles from "./ImageUpload.module.scss";
import { removeFile, uploadMultipleFiles, uploadSingleFile } from "../../../../api-handle/fileApi";

const FILE_BASE_URL = (process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001").replace(/\/?$/, "/");

const ImageUpload = ({ formData, setFormData, type, errors }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  // useEffect(() => { //"Form Data : ", formData) }, [formData]);

  const handleFileUpload = async (e, field, type) => {
    setError("");
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (field === "images") {
      const currentCount = formData[`${type}Detail`]?.images?.length || 0;
      if (currentCount + files.length > 5) {
        setError("You can upload a maximum of 5 images.");
        return;
      }
    }

    if (field === "videos") {
      const currentCount = formData[`${type}Detail`]?.videos?.length || 0;
      if (currentCount + files.length > 3) {
        setError("You can upload a maximum of 3 videos.");
        return;
      }
      for (const file of files) {
        if (file.size > 200 * 1024 * 1024) {
          setError("Each video must be 200MB or smaller.");
          return;
        }
      }
    }

    setUploading(true);
    try {
      let uploadedFiles = [];

      if (field === "coverImage") {
        if (files.length > 1) {
          setError("You can only upload one cover image.");
          setUploading(false);
          return;
        }
        const response = await uploadSingleFile(files[0]);
        uploadedFiles = [response.fileName];
      } else if (field === "documents") {
        const response = await uploadMultipleFiles(files);
        uploadedFiles = response;
        setFormData(prevData => ({
          ...prevData,
          documents: [...(prevData.documents || []), ...uploadedFiles]
        }));
        setUploading(false);
        return;
      } else if (field === "contractDocument") {
        const response = await uploadSingleFile(files[0]);
        setFormData(prevData => ({
          ...prevData,
          contractDocument: response.fileName,
        }));
        setUploading(false);
        return;
      } else {
        const response = await uploadMultipleFiles(files);
        uploadedFiles = response;
      }

      setFormData(prevData => ({
        ...prevData,
        [`${type}Detail`]: {
          ...prevData[`${type}Detail`],
          [field]: field === "coverImage" ? uploadedFiles[0] : [...(prevData[`${type}Detail`]?.[field] || []), ...uploadedFiles],
        },
      }));
    } catch (err) {
      setError("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async (field, index = null, type) => {
    try {
      let fileName;
      if (field === "coverImage") {
        fileName = formData[`${type}Detail`]?.coverImage;
      } else if (field === "documents") {
        fileName = formData.documents[index];
      } else if (field === "contractDocument") {
        fileName = formData.contractDocument;
      } else {
        fileName = formData[`${type}Detail`]?.[field][index];
      }

      await removeFile(fileName);

      setFormData(prevData => {
        let updatedData = { ...prevData };
        if (field === "coverImage") {
          updatedData[`${type}Detail`].coverImage = "";
        } else if (field === "documents") {
          updatedData.documents = prevData.documents.filter((_, i) => i !== index);
        } else if (field === "contractDocument") {
          updatedData.contractDocument = "";
        } else {
          updatedData[`${type}Detail`][field] = prevData[`${type}Detail`][field].filter((_, i) => i !== index);
        }
        return updatedData;
      });
    } catch (err) {
      setError("Failed to remove file. Please try again.");
    }
  };

  return (
    <>
      {
        type.length > 0 &&
        <div className={styles.uploadContainer}>

          {error && (
            <div className={styles.errorMessage}>
              <AlertCircle size={16} className={styles.errorIcon} /> {error}
            </div>
          )}
          <Row>
            {/* Loop through types and show upload sections for each type */}
            {type.map((t, index) => (
                <React.Fragment key={index}>
                  {t !== "marketplace" && (
                    <Col md={12} className={styles.uploadField} data-error-field={`${t}CoverImage`}>
                      <Label>{`Upload ${t.charAt(0).toUpperCase() + t.slice(1)} Cover Image`}</Label>
                      <label className={styles.uploadBox} htmlFor={`coverImageInput_${t}`}>
                        <Upload size={20} className={styles.uploadIcon} /> Click to Upload
                      </label>
                      <Input
                        id={`coverImageInput_${t}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, "coverImage", t)}
                        className={styles.uploadInput}
                        style={{ display: "none" }}
                        disabled={uploading}
                      />
                      {formData[`${t}Detail`]?.coverImage && (
                        <div className={styles.previewItem}>
                          <img src={`${FILE_BASE_URL}${formData[`${t}Detail`].coverImage}`} alt="cover" className={styles.previewImage} />
                          <XCircle size={14} className={styles.removeIcon} onClick={() => handleRemoveFile("coverImage", 0, t)} />
                        </div>
                      )}
                      {errors?.[`${t}CoverImage`] && (
                        <div className="text-danger small mt-1">{errors[`${t}CoverImage`]}</div>
                      )}
                    </Col>
                  )}

                  <Col md={12} className={styles.uploadField}>
                    <Label>{`Upload ${t.charAt(0).toUpperCase() + t.slice(1)} Additional Images`}</Label>
                    <label className={styles.uploadBox} htmlFor={`imagesInput_${t}`}>
                      <ImageIcon size={20} className={styles.uploadIcon} /> Click to Upload
                    </label>
                    <Input
                      id={`imagesInput_${t}`}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, "images", t)}
                      className={styles.uploadInput}
                      style={{ display: "none" }}
                      disabled={uploading}
                    />
                    <div className={styles.previewList}>
                      {formData[`${t}Detail`]?.images?.map((fileName, i) => (
                        <div key={i} className={styles.previewItem}>
                          <img src={`${FILE_BASE_URL}${fileName}`} alt={`preview-${i}`} className={styles.previewImage} />
                          <XCircle size={14} className={styles.removeIcon} onClick={() => handleRemoveFile("images", i, t)} />
                        </div>
                      ))}
                    </div>
                  </Col>

                  <Col md={12} className={styles.uploadField}>
                    <Label>{`Upload ${t.charAt(0).toUpperCase() + t.slice(1)} Videos`}</Label>
                    <label className={styles.uploadBox} htmlFor={`videosInput_${t}`}>
                      <Film size={20} className={styles.uploadIcon} /> Click to Upload
                    </label>
                    <Input
                      id={`videosInput_${t}`}
                      type="file"
                      multiple
                      accept="video/*"
                      onChange={(e) => handleFileUpload(e, "videos", t)}
                      className={styles.uploadInput}
                      style={{ display: "none" }}
                      disabled={uploading}
                    />
                    <div className={styles.previewList}>
                      {formData[`${t}Detail`]?.videos?.map((fileName, i) => (
                        <div key={i} className={styles.previewItem}>
                          <video src={`${FILE_BASE_URL}${fileName}`} className={styles.previewImage} controls preload="metadata" />
                          <XCircle size={14} className={styles.removeIcon} onClick={() => handleRemoveFile("videos", i, t)} />
                        </div>
                      ))}
                    </div>
                  </Col>
                </React.Fragment>
              ))}
            {/* Documents Upload (Global for all types) */}
            {type.length > 0 && (
              <Col md={12} className={styles.uploadField} data-error-field="documents">
                <Label>Upload Documents</Label>
                <label className={styles.uploadBox} htmlFor="documentsInput">
                  <FileText size={20} className={styles.uploadIcon} /> Click to Upload
                </label>
                <Input
                  id="documentsInput"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileUpload(e, "documents")}
                  className={styles.uploadInput}
                  style={{ display: "none" }}
                  disabled={uploading}
                />
                <div className={styles.previewList}>
                  {formData?.documents?.map((fileName, i) => (
                    <div key={i} className={styles.previewItem}>
                      <iframe
                        src={`https://docs.google.com/gview?url=${FILE_BASE_URL}${fileName}&embedded=true`}
                        className={styles.documentFrame}
                        title="Document Preview"
                      />
                      <XCircle size={14} className={styles.removeIcon} onClick={() => handleRemoveFile("documents", i)} />
                    </div>
                  ))}
                </div>
                {errors?.documents && (
                  <div className="text-danger small mt-1">{errors.documents}</div>
                )}
              </Col>
            )}

            {/* Contract Document (single PDF) — grouped with the other docs and,
                like them, shown only once a business type is selected. */}
            {type.length > 0 && (
              <Col md={12} className={styles.uploadField} data-error-field="contractDocument">
                <Label>Contract Document (PDF)</Label>
                <label className={styles.uploadBox} htmlFor="contractDocumentInput">
                  <FileText size={20} className={styles.uploadIcon} /> Click to Upload
                </label>
                <Input
                  id="contractDocumentInput"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => handleFileUpload(e, "contractDocument")}
                  className={styles.uploadInput}
                  style={{ display: "none" }}
                  disabled={uploading}
                />
                {formData?.contractDocument && (
                  <div className={styles.previewList}>
                    <div className={styles.previewItem}>
                      <iframe
                        src={`https://docs.google.com/gview?url=${FILE_BASE_URL}${formData.contractDocument}&embedded=true`}
                        className={styles.documentFrame}
                        title="Contract Preview"
                      />
                      <XCircle size={14} className={styles.removeIcon} onClick={() => handleRemoveFile("contractDocument")} />
                    </div>
                  </div>
                )}
                {errors?.contractDocument && (
                  <div className="text-danger small mt-1">{errors.contractDocument}</div>
                )}
              </Col>
            )}

          </Row>
          {uploading && <p className={styles.uploadingText}>Uploading...</p>}
        </div>
      }
    </>
  );
};

export default ImageUpload;
